/**
 * Save Round-Trip Verifier
 *
 * Simulates a save→load cycle entirely in-memory and compares the result
 * to the original store state. Any field that doesn't survive the round-trip
 * is reported as a potential bug.
 *
 * Usage (dev console):  window.__verifyScopeSave?.()
 * Or via the dev-mode "Verify Save" button in the scope page header.
 */

import type { ScopeData } from '../types'
import { mapStoreStateToDbScope, mapDbScopeToStoreState } from './mappers'

export interface FieldDiff {
  path: string
  before: unknown
  after: unknown
  issue: 'missing' | 'changed' | 'added'
}

/**
 * Deep-compare two values and collect all differences.
 * Returns an array of diff descriptors for every leaf that changed.
 */
function deepDiff(a: unknown, b: unknown, path = ''): FieldDiff[] {
  const diffs: FieldDiff[] = []

  // Identical primitives or both null/undefined
  if (a === b) return diffs

  // One is null/undefined and the other isn't
  if (a == null || b == null) {
    // Ignore null vs '' vs 0 vs false (normalisation artifacts)
    if (isEmptyish(a) && isEmptyish(b)) return diffs
    diffs.push({ path, before: a, after: b, issue: b == null ? 'missing' : 'changed' })
    return diffs
  }

  // Arrays
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) {
      diffs.push({ path, before: a, after: b, issue: 'changed' })
      return diffs
    }
    const maxLen = Math.max(a.length, b.length)
    for (let i = 0; i < maxLen; i++) {
      diffs.push(...deepDiff(a[i], b[i], `${path}[${i}]`))
    }
    return diffs
  }

  // Objects
  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const allKeys = new Set([...Object.keys(aObj), ...Object.keys(bObj)])

    for (const key of allKeys) {
      const childPath = path ? `${path}.${key}` : key
      if (!(key in bObj)) {
        diffs.push({ path: childPath, before: aObj[key], after: undefined, issue: 'missing' })
      } else if (!(key in aObj)) {
        diffs.push({ path: childPath, before: undefined, after: bObj[key], issue: 'added' })
      } else {
        diffs.push(...deepDiff(aObj[key], bObj[key], childPath))
      }
    }
    return diffs
  }

  // Primitive mismatch (but treat normalised equivalents as OK)
  if (isEmptyish(a) && isEmptyish(b)) return diffs
  diffs.push({ path, before: a, after: b, issue: 'changed' })
  return diffs
}

/** Treat '', 0, false, null, undefined, 'unknown', 'tbd' as "empty-ish" for comparison tolerance */
function isEmptyish(v: unknown): boolean {
  if (v == null) return true
  if (v === '') return true
  if (v === 0) return true
  if (v === false) return true
  return false
}

export interface VerifyResult {
  ok: boolean
  diffs: FieldDiff[]
  /** Only meaningful diffs (excludes normalisation noise) */
  problems: FieldDiff[]
  summary: string
}

/**
 * Run a save→load round-trip on the given scope data and report any field loss.
 *
 * The function:
 * 1. Serialises `scope` via mapStoreStateToDbScope (→ JSON strings)
 * 2. Creates a fake Scope API row from those strings
 * 3. Deserialises via mapDbScopeToStoreState
 * 4. Deep-compares the original to the round-tripped version
 */
export function verifySaveRoundTrip(scope: ScopeData): VerifyResult {
  // Step 1: Serialise (same as the real save path)
  const dbShape = mapStoreStateToDbScope(scope)

  // Step 2: Reconstruct a fake DB row that mapDbScopeToStoreState expects
  const fakeDbRow = {
    id: 1,
    deal_id: 1,
    customer_name: dbShape.customer_name ?? null,
    customer_abn: (dbShape as any).customer_abn ?? null,
    general_info: dbShape.general_info ?? null,
    kyc_config: dbShape.kyc_config ?? null,
    aml_config: dbShape.aml_config ?? null,
    biometrics_config: dbShape.biometrics_config ?? null,
    kyb_config: dbShape.kyb_config ?? null,
    recipes_config: dbShape.recipes_config ?? null,
    rules_config: dbShape.rules_config ?? null,
    sow_config: (dbShape as any).sow_config ?? null,
    notes: dbShape.notes ?? null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }

  // Step 3: Deserialise (same as the real load path)
  const roundTripped = mapDbScopeToStoreState(fakeDbRow as any)

  // Step 4: Compare
  // Only compare config sections that go through the save/load cycle.
  // scopeHours and featureRequests are currently not persisted via this path.
  const configKeys: Array<keyof ScopeData> = [
    'customerName',
    'customerAbn',
    'generalInfo',
    'kycConfig',
    'amlConfig',
    'biometricsConfig',
    'kybConfig',
    'rulesConfig',
    'riskConfig',
    'sowConfig',
    'recipesConfig',
    'workflowsConfig',
    'notes',
  ]

  let allDiffs: FieldDiff[] = []
  for (const key of configKeys) {
    allDiffs.push(...deepDiff(scope[key], roundTripped[key], key))
  }

  // Filter out normalisation noise (empty-ish defaults that get normalised)
  const problems = allDiffs.filter((d) => {
    // Skip diffs where both sides are empty-ish
    if (isEmptyish(d.before) && isEmptyish(d.after)) return false
    // Skip 'added' issues (extra defaults added is fine)
    if (d.issue === 'added' && isEmptyish(d.after)) return false
    return true
  })

  const ok = problems.length === 0

  const summary = ok
    ? `✅ All ${configKeys.length} config sections survived the save→load round-trip.`
    : `⚠️ ${problems.length} field(s) changed or were lost during save→load:\n` +
      problems
        .map((d) => `  • ${d.path}: ${JSON.stringify(d.before)} → ${JSON.stringify(d.after)} (${d.issue})`)
        .join('\n')

  return { ok, diffs: allDiffs, problems, summary }
}

/**
 * Pretty-print the verification result to the console with colours.
 */
export function logVerifyResult(result: VerifyResult): void {
  if (result.ok) {
    console.log('%c' + result.summary, 'color: green; font-weight: bold')
  } else {
    console.warn('%c' + result.summary, 'color: orange; font-weight: bold')
    console.table(result.problems.map((d) => ({
      Path: d.path,
      Before: typeof d.before === 'object' ? JSON.stringify(d.before) : d.before,
      After: typeof d.after === 'object' ? JSON.stringify(d.after) : d.after,
      Issue: d.issue,
    })))
  }
}
